import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";

/**
 * Entry point. Two independent pages live in this app:
 *
 *   /           the course checkout  (<App />, unchanged)
 *   /business   The Marketing Control Room  (B2B offer)
 *
 * Deliberately NOT react-router, even though it is already a dependency.
 * Routing at the entry point puts the router in the main chunk, which every
 * COURSE buyer downloads at "/" — measured at +12.8 kB gzipped for a funnel
 * that never navigates anywhere. There is nothing to route: the checkout is a
 * state-driven step wizard, and these two pages are separate entry points
 * reached directly by URL, never by in-app navigation.
 *
 * So: read the path once, render one of two trees. BusinessCheckout is lazy
 * so its ~20 kB stays in its own chunk and "/" ships exactly what it always
 * shipped. The course funnel's bundle is unchanged by this offer.
 *
 * Anything that is not /business falls through to <App />, so old links,
 * mistyped paths and the ?path=/?xid=/fbclid query strings the landing pages
 * append all behave exactly as before.
 */
const BusinessCheckout = lazy(() => import("./pages/business/BusinessCheckout"));

function isBusinessRoute() {
  try {
    // Served under www.sajawal.school/checkout (proxied) — and, for legacy
    // links, at the old subdomain root. Both spellings mean the B2B page.
    const path = window.location.pathname.replace(/\/+$/, "").toLowerCase();
    return path === "/business" || path === "/checkout/business";
  } catch {
    return false;
  }
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {isBusinessRoute() ? (
      /* Fallback matches the page's own background so the split-chunk fetch
         reads as loading rather than as a flash of white. */
      <Suspense
        fallback={<div style={{ minHeight: "100vh", background: "#f9fafb" }} />}
      >
        <BusinessCheckout />
      </Suspense>
    ) : (
      <App />
    )}
  </React.StrictMode>
);
